import { useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button, H5, Modal } from '@pay-com/components'
import { CountryAlpha2 } from 'generated/graphql'
import {
  BeneficialOwner,
  beneficialOwnerSchema
} from 'pages/Onboarding/OnboardingRegion/UsRegion/UsRegionStepsConfigs/BeneficialOwner/beneficial-owner-schema'
import { ControlledFields } from 'components/ControlledFields'
import { TooltipFieldProvider } from 'components/TooltipFieldWrapper'
import { getBeneficialOwnerModalConfig } from './beneficial-owner-config'

type BeneficialOwnerModalProps = {
  isOpen: boolean
  defaultValues?: BeneficialOwner
  isEdit?: boolean
  isSaving?: boolean
  signerTaken?: boolean
  contactTaken?: boolean
  onConfirm: (owner: BeneficialOwner) => void
  onDismiss: () => void
}

const emptyDefaults = {
  individual: {
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    nationalities: [CountryAlpha2.Us],
    email: '',
    phoneNumber: '',
    residentialAddress: {
      countryAlpha2: CountryAlpha2.Us,
      addressLine: '',
      city: '',
      zip: ''
    }
  },
  relation: {
    title: '',
    percentageOfShares: '',
    signsTaxDocuments: false,
    isContact: false
  }
}

export const BeneficialOwnerModal = ({
  isOpen,
  defaultValues,
  isEdit = false,
  isSaving = false,
  signerTaken = false,
  contactTaken = false,
  onConfirm,
  onDismiss
}: BeneficialOwnerModalProps) => {
  const { t } = useTranslation()
  const config = getBeneficialOwnerModalConfig(t, {
    disableSigner: signerTaken,
    disableContact: contactTaken
  })

  const methods = useForm<BeneficialOwner>({
    resolver: zodResolver(beneficialOwnerSchema),
    defaultValues: (defaultValues ?? emptyDefaults) as BeneficialOwner,
    mode: 'onBlur',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    if (isOpen) {
      methods.reset((defaultValues ?? emptyDefaults) as BeneficialOwner)
    }
  }, [isOpen, defaultValues])

  const handleSubmit = methods.handleSubmit(data => {
    onConfirm(data)
  })

  return (
    <Modal.Root open={isOpen} onOpenChange={open => !open && onDismiss()}>
      <Modal className="flex h-[40rem] max-h-[90vh] flex-col">
        <Modal.Header>
          <H5 className="!text-[26px]">
            {isEdit
              ? t('beneficialOwner.modal.editTitle')
              : t('beneficialOwner.modal.addTitle')}
          </H5>
        </Modal.Header>
        <Modal.Content className="modal-scrollbar flex-1 overflow-y-auto">
          <FormProvider {...methods}>
            <TooltipFieldProvider>
              <div className="flex flex-col gap-4">
                <ControlledFields fields={config.fields} />
              </div>
            </TooltipFieldProvider>
          </FormProvider>
        </Modal.Content>
        <Modal.Actions className="mt-auto pt-4">
          <Button
            variant="text"
            type="button"
            onClick={onDismiss}
            disabled={isSaving}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={isSaving}
          >
            {isEdit ? t('actions.save') : t('beneficialOwner.modal.addButton')}
          </Button>
        </Modal.Actions>
      </Modal>
    </Modal.Root>
  )
}
